'use server'

import { GetStaticProps, GetStaticPropsContext, GetServerSideProps, GetServerSidePropsContext } from 'next'
import fs from 'fs'
import { dirname } from 'path'

const IS_PLAY_WRITE_CONTEXT = process.env.NEXT_PUBLIC_IS_PLAY_WRITE_CONTEXT
const GENERATE_MOCK_ON_PAGE_VISIT = process.env.NEXT_PUBLIC_GENERATE_MOCK_ON_PAGE_VISIT

function saveAsMock(filePath: string, data: Record<string, any>) {
  const dirPath = dirname(filePath)
  if (!fs.existsSync(dirPath)) {
    fs.mkdirSync(dirPath, { recursive: true })
  }
  fs.writeFileSync(filePath, JSON.stringify(data, null, 2))
}

function extractMockPath(context: GetStaticPropsContext | GetServerSidePropsContext, pagePath: string): string {
  const tenant = context.locale as string
  const params = context.params ?? {}

  if (pagePath.startsWith('sitemap')) {
    const segments = Array.isArray(context.params?.sitemap) ? context.params!.sitemap : []

    const slug = segments[0] || 'specialists'

    const last = segments[segments.length - 1]
    const isPage = last?.startsWith('page-')
    const page = isPage ? last.replace('page-', '') : '1'

    const maybeSorting = segments[1]
    const sorting = maybeSorting && !maybeSorting.startsWith('page-') ? maybeSorting : null

    const keyParts = [slug]
    if (sorting) {
      keyParts.push(`sort-${sorting}`)
    }
    if (page !== '1') {
      keyParts.push(`page-${page}`)
    }

    const key = keyParts.join('-')
    return `playwright/mocks/${tenant}/sitemap/${key}.json`
  }

  const paths = [tenant, ...pagePath.split('/')]

  const nativePath = paths
    .map((segment) => (segment.startsWith(':') ? params[segment.substring(1)] : segment))
    .join('/')

  if (!pagePath) {
    return `playwright/mocks/${tenant}/homepage/base-case.json`
  }
  return `playwright/mocks/${nativePath}.json`
}

function readMockFile(jsonPath: string) {
  try {
    const json = fs.readFileSync(jsonPath, 'utf-8')
    return JSON.parse(json)
  } catch {
    return {
      props: {
        errorCode: '418',
        errorText: `No mock file found at '${jsonPath}'`
      }
    }
  }
}

export function withStaticPropsMocks<T extends { [key: string]: any }>(
  getStaticProps: GetStaticProps<T>,
  pagePath: string
) {
  return async (context: GetStaticPropsContext) => {
    if (GENERATE_MOCK_ON_PAGE_VISIT) {
      const jsonPath = extractMockPath(context, pagePath)
      const props = await getStaticProps(context)
      saveAsMock(jsonPath, props)
      return props
    }

    if (IS_PLAY_WRITE_CONTEXT) {
      const jsonPath = extractMockPath(context, pagePath)
      return readMockFile(jsonPath)
    }

    return getStaticProps(context)
  }
}

export function withSSRPropsMocks<T extends { [key: string]: any }>(
  getServerSideProps: GetServerSideProps<T>,
  pagePath: string
) {
  return async (context: GetServerSidePropsContext) => {
    if (GENERATE_MOCK_ON_PAGE_VISIT) {
      const jsonPath = extractMockPath(context, pagePath)
      const props = await getServerSideProps(context)
      saveAsMock(jsonPath, props)
      return props
    }

    if (IS_PLAY_WRITE_CONTEXT) {
      const jsonPath = extractMockPath(context, pagePath)
      return readMockFile(jsonPath)
    }

    return getServerSideProps(context)
  }
}
